import { service, servicePackage } from "@/views/management/services/services.lib";
import { defaultRosterFromShop, ROSTER_DAYS } from "@/helpers/rosterDefaults";

export const ONBOARD_TRADES = [
  { id: "barber", label: "Barber" },
  { id: "hair", label: "Hair salon" },
  { id: "beauty", label: "Beauty" },
  { id: "other", label: "Something else" }
];

/**
 * First-run only for auto-provisioned SaaS books (store id === auth uid).
 * Existing tenants such as boxx-finglas use a real store id and never match.
 * Returns "needed" | "done" | "unknown" — unknown means do not redirect yet.
 */
export function onboardState({ shop, settings, userId }) {
  if (!userId) return "unknown";
  if (settings && settings.onboarding && settings.onboarding.completedAt) {
    return "done";
  }
  if (!shop) return "unknown";
  const storeId = shop._id || shop.storeId;
  if (!storeId) return "unknown";
  if (storeId !== userId) return "done";
  return "needed";
}

export function needsOnboarding(args) {
  return onboardState(args) === "needed";
}

export function isUserVerified(user) {
  if (!user) return false;
  if (user.emailVerified) return true;
  const providers = user.providerData || [];
  return providers.some(
    provider => provider && provider.providerId === "google.com"
  );
}

const VERIFY_TOKEN_KEY = "sb.verifyToken";

function tokenStoreGet() {
  if (typeof window === "undefined") return null;
  try {
    return (
      (window.localStorage && window.localStorage.getItem(VERIFY_TOKEN_KEY)) ||
      (window.sessionStorage && window.sessionStorage.getItem(VERIFY_TOKEN_KEY))
    );
  } catch (error) {
    return null;
  }
}

function tokenStoreSet(token) {
  if (typeof window === "undefined" || !token) return;
  try {
    if (window.localStorage) window.localStorage.setItem(VERIFY_TOKEN_KEY, token);
    if (window.sessionStorage) {
      window.sessionStorage.setItem(VERIFY_TOKEN_KEY, token);
    }
  } catch (error) {
    /* private mode */
  }
}

export function tokenFromSearch(search) {
  if (!search) return "";
  const match = /[?&]token=([^&]+)/.exec(String(search));
  if (!match || !match[1]) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch (error) {
    return match[1];
  }
}

export function readStoredVerifyToken() {
  return tokenStoreGet();
}

function locationHaystack(route, loc) {
  return [
    route && route.path,
    route && route.fullPath,
    loc && loc.pathname,
    loc && loc.href,
    loc && loc.hash
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isOtherTokenPath(route, loc) {
  return /reset-password|pay-invoice|pay-now/.test(locationHaystack(route, loc));
}

export function captureVerifyToken(route, loc) {
  const location =
    loc || (typeof window !== "undefined" ? window.location : null);
  if (isOtherTokenPath(route, location)) {
    return tokenStoreGet();
  }

  let token = route && route.query ? route.query.token : null;
  if (Array.isArray(token)) token = token[0];
  if (token != null && token !== true) token = String(token);
  else token = "";

  if (!token) {
    const search = (location && location.search) || "";
    const hash = (location && location.hash) || "";
    const href = (location && location.href) || "";
    token =
      tokenFromSearch(search) ||
      tokenFromSearch(hash) ||
      tokenFromSearch(href);
  }

  if (token) {
    tokenStoreSet(token);
    return token;
  }
  return tokenStoreGet();
}

export function clearStoredVerifyToken() {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage) window.localStorage.removeItem(VERIFY_TOKEN_KEY);
    if (window.sessionStorage) {
      window.sessionStorage.removeItem(VERIFY_TOKEN_KEY);
    }
  } catch (error) {
    /* private mode */
  }
}

export function persistAuthUser(user) {
  if (!user) return null;
  const providers = (user.providerData || []).map(provider => ({
    providerId: provider.providerId,
    email: provider.email || ""
  }));
  return {
    uid: user.uid,
    email: user.email || "",
    displayName: user.displayName || "",
    emailVerified: isUserVerified(user),
    providerData: providers
  };
}

export function emptyOnboardDraft(seed) {
  const src = seed || {};
  return {
    name: src.name || "",
    email: src.email || "",
    password: "",
    shopName: src.shopName || "",
    town: src.town || "",
    trade: src.trade || "barber",
    days: src.days || {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: false
    },
    weekday: src.weekday || { start: "09:00", end: "18:00" },
    saturday: src.saturday || { start: "09:00", end: "17:00" },
    sunday: src.sunday || { start: "10:00", end: "16:00" },
    personName: src.personName || "",
    takeBookings: src.takeBookings !== false,
    menu: src.menu || starterMenu(src.trade || "barber")
  };
}

export function tradeLabel(tradeId) {
  const trade = ONBOARD_TRADES.find(item => item.id === tradeId);
  return trade ? trade.label : "Shop";
}

export function starterMenu(trade) {
  if (trade === "hair") {
    return [
      item("cut", "Cut", 45, 15, 35, true),
      item("blow", "Blow dry", 30, 15, 22, true),
      item("colour", "Colour", 90, 15, 65, false)
    ];
  }
  if (trade === "beauty") {
    return [
      item("lashes", "Lashes", 45, 15, 35, true),
      item("nails", "Nails", 45, 15, 30, true),
      item("brows", "Brows", 20, 10, 15, false)
    ];
  }
  return [
    item("cut", "Haircut", 30, 30, 22, true),
    item("combo", "Haircut & beard", 40, 30, 32, true),
    item("kids", "Kids cut", 30, 30, 18, false)
  ];
}

function item(key, name, duration, interval, price, on) {
  return { key, name, duration, interval, price, on };
}

export function hoursForDay(draft, day) {
  if (day === "saturday") return draft.saturday;
  if (day === "sunday") return draft.sunday;
  return draft.weekday;
}

export function shopFromDraft(shop, draft) {
  const daysOpen = {};
  const openingTimes = {};
  const closingTimes = {};
  ROSTER_DAYS.forEach(day => {
    const open = !!(draft.days && draft.days[day]);
    daysOpen[day] = open;
    if (!open) {
      openingTimes[day] = "";
      closingTimes[day] = "";
      return;
    }
    const hours = hoursForDay(draft, day) || {};
    openingTimes[day] = hours.start || "09:00";
    closingTimes[day] = hours.end || "18:00";
  });
  const label = tradeLabel(draft.trade);
  const town = (draft.town || "").trim();
  return {
    ...(shop || {}),
    name: String(draft.shopName || "").trim(),
    description: town ? `${label} in ${town}` : label,
    daysOpen,
    openingTimes,
    closingTimes
  };
}

export function employeeFromDraft(draft, shop) {
  const name = String(draft.personName || "").trim();
  if (!name || draft.takeBookings === false) return null;
  return {
    name,
    description: "",
    availableForOnlineBookings: true,
    enabled: true,
    image: null,
    imageGreyscale: false,
    tipUrl: null,
    ...defaultRosterFromShop(shop)
  };
}

export function servicesFromDraft(draft, resourceId) {
  const selected = (draft.menu || []).filter(
    row => row && row.on && String(row.name || "").trim()
  );
  return selected.map(row => {
    const pkg = servicePackage({
      name: String(row.name).trim(),
      price: Number(row.price) || 0
    });
    return service({
      name: String(row.name).trim(),
      duration: Number(row.duration) || 30,
      durationUnit: "MINUTES",
      displayDuration: Number(row.interval) || 30,
      displayDurationUnit: "MINUTES",
      enabled: true,
      availableForOnlineBookings: true,
      resources: resourceId ? [resourceId] : [],
      packages: { [pkg._id]: pkg },
      packagesDisplayOrder: [pkg._id]
    });
  });
}

export function onboardSummary(draft) {
  const openDays = ROSTER_DAYS.filter(day => draft.days && draft.days[day]);
  const menu = (draft.menu || []).filter(row => row && row.on);
  return {
    shopName: String(draft.shopName || "").trim(),
    trade: tradeLabel(draft.trade),
    town: String(draft.town || "").trim(),
    openDays,
    personName: String(draft.personName || "").trim(),
    takeBookings: draft.takeBookings !== false,
    menuCount: menu.length
  };
}

export function draftIsReadyToSave(draft) {
  return !!(draft && String(draft.shopName || "").trim());
}

/** Firestore list rules require storeId. A resourceId-only query is denied. */
export function scopedResourceFilters(storeId, resourceId) {
  if (!storeId || !resourceId) return null;
  return [
    ["storeId", "==", storeId],
    ["resourceId", "==", resourceId]
  ];
}

/**
 * What Open the book should write. Reuses staff/services from a failed first tap.
 */
export function planOnboardPersist({ draft, shop, employees, services }) {
  const nextShop = shopFromDraft(shop, draft);
  const person = employeeFromDraft(draft, nextShop);
  const staff = employees || [];
  const existingEmployee = person
    ? staff.find(row => row && row.name === person.name)
    : null;
  const resourceId = existingEmployee ? existingEmployee._id : null;
  const existingNames = {};
  (services || []).forEach(row => {
    if (row && row.name) existingNames[row.name] = true;
  });
  const servicesToCreate = servicesFromDraft(draft, resourceId).filter(
    row => row && row.name && !existingNames[row.name]
  );
  return {
    nextShop,
    person: existingEmployee ? null : person,
    reuseEmployeeId: resourceId,
    servicesToCreate
  };
}
